/**
 * Marker presets and sequencing (pure). A MARK is created instantly with a provisional label
 * ("MARK 3"); a preset relabels it afterwards without touching its timestamp.
 */
import { formatLocal, formatUtc, MINUS, type EpochMs, type Marker, type MarkerCategory, type Mission } from '@apos/shared';

export type PresetId = 'pass' | 'hero' | 'landing' | 'missed' | 'issue' | 'note';

export interface MarkerPreset {
  id: PresetId;
  category: MarkerCategory;
  /** Fixed label, or computed from existing markers (PASS n). */
  label(markers: readonly Pick<Marker, 'label' | 'category'>[]): string;
  /** Opens the free-text field instead of applying immediately. */
  wantsText?: boolean;
}

const numbered = (prefix: string) => new RegExp(`^${prefix}\\s+(\\d+)$`, 'i');

/** 1 + the highest "PREFIX n" label seen (so gaps and deletions never reuse a number). */
export function nextSequence(markers: readonly Pick<Marker, 'label'>[], prefix: string): number {
  const re = numbered(prefix);
  let max = 0;
  for (const m of markers) {
    const hit = re.exec(m.label.trim());
    if (hit) max = Math.max(max, Number(hit[1]));
  }
  return max + 1;
}

/**
 * Next pass number. A HERO PASS is also a pass, so PASS 1, PASS 2, PASS 3, HERO PASS → PASS 5.
 * Uses whichever is larger: the highest "PASS n" or the count of pass-like markers.
 */
export function nextPassNumber(markers: readonly Pick<Marker, 'label' | 'category'>[]): number {
  const byLabel = nextSequence(markers, 'PASS') - 1;
  const count = markers.filter((m) => m.category === 'pass' || m.category === 'hero').length;
  return Math.max(byLabel, count) + 1;
}

export const MARKER_PRESETS: readonly MarkerPreset[] = [
  { id: 'pass', category: 'pass', label: (ms) => `PASS ${nextPassNumber(ms)}` },
  { id: 'hero', category: 'hero', label: () => 'HERO PASS' },
  { id: 'landing', category: 'good', label: () => 'GREAT LANDING' },
  { id: 'missed', category: 'missed', label: () => 'MISSED SHOT' },
  { id: 'issue', category: 'issue', label: () => 'CAMERA ISSUE' },
  { id: 'note', category: 'note', label: () => 'NOTE', wantsText: true },
];

/** Next one-tap mark number: 1 + the highest "M-n" (or legacy "MARK n") label. */
export function nextMarkNumber(markers: readonly Pick<Marker, 'label'>[]): number {
  let max = 0;
  for (const m of markers) {
    const hit = /^M-(\d+)$/i.exec(m.label.trim()) ?? /^MARK\s+(\d+)$/i.exec(m.label.trim());
    if (hit) max = Math.max(max, Number(hit[1]));
  }
  return max + 1;
}

/** Label for a one-tap MARK ("M-14"). Categories are added after the flight, never during. */
export function quickMarkLabel(markers: readonly Pick<Marker, 'label'>[]): string {
  return `M-${nextMarkNumber(markers)}`;
}

/** Two presses within this window make one Hero mark (spec §8.4). */
export const HERO_DOUBLE_PRESS_MS = 400;
/** The Undo chip stays this long after a mark (spec §8.4). */
export const UNDO_WINDOW_MS = 5000;
/** The confirmation flash on the MARK button (spec §8.3). */
export const MARK_FLASH_MS = 2500;

export type PressAction = { kind: 'new' } | { kind: 'hero'; of: number };

/**
 * Decide what a MARK press does: a press within 400 ms of the previous press upgrades that mark
 * to Hero instead of creating a second one; anything else is a new mark.
 */
export function classifyPress(prevPressAt: number | null, prevWasHero: boolean, pressAt: number): PressAction {
  if (prevPressAt !== null && !prevWasHero && pressAt - prevPressAt >= 0 && pressAt - prevPressAt <= HERO_DOUBLE_PRESS_MS) return { kind: 'hero', of: prevPressAt };
  return { kind: 'new' };
}

/** Markers other than `exclude` (the one being relabelled must not count towards its own number). */
export function othersThan<T extends { id: string }>(markers: readonly T[], exclude: string | null): T[] {
  return exclude ? markers.filter((m) => m.id !== exclude) : [...markers];
}

/** Mission elapsed "T+12:04" / "T+1:02:09" / "T−0:30" relative to the actual (or planned) start. */
export function missionElapsed(t: EpochMs, mission: Pick<Mission, 'actualStart' | 'windowStart'>): string {
  const start = mission.actualStart ?? mission.windowStart;
  const d = Math.round((t - start) / 1000);
  const s = Math.abs(d);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  const body = h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${String(m).padStart(2, '0')}:${ss}`;
  return `T${d < 0 ? MINUS : '+'}${body}`;
}

export interface MarkerTimes {
  utc: string;
  local: string;
  elapsed: string;
}

export function markerTimes(t: EpochMs, mission: Pick<Mission, 'actualStart' | 'windowStart' | 'timezone'>): MarkerTimes {
  return { utc: formatUtc(t), local: formatLocal(t, mission.timezone), elapsed: missionElapsed(t, mission) };
}

export const CATEGORY_TONE: Record<MarkerCategory, 'neutral' | 'strong' | 'live' | 'alert' | 'aging' | 'sun'> = {
  pass: 'strong',
  hero: 'sun',
  good: 'live',
  missed: 'aging',
  issue: 'alert',
  note: 'neutral',
};
